// Sugalarm — cycle 67: dev-only Alarm Test Console.
//
// Drives synthetic readings through the real alerts pipeline via
// `POST /api/dev/inject-reading` so cross-device Stop / Snooze /
// engine-re-arm behavior can be reproduced on two clients in
// seconds, instead of waiting for a real CGM low.
//
// The component is dev-gated at two layers:
//   1. Server gate — `/api/dev/inject-reading` 404s when
//      `SUGALARM_DEV_INJECT_ENABLED` is unset; the call fails and we
//      surface that as an error pill.
//   2. Mount gate — the parent (`SettingsScreen`) only renders this
//      component when `useDevInjectEnabled()` resolved `true` from
//      `/api/health.dev_inject_enabled`. Production builds never
//      mount it.
//
// Live state readout. The console subscribes to the live alarm
// `episode` and prints `phase / level / silenced / snoozed_until /
// alertId / audioOn` so two side-by-side clients can be compared
// frame-by-frame — exactly the cross-device observability that has
// been missing.

import { useCallback, useEffect, useRef, useState } from "react";

import * as devApi from "../../api/dev";
import { sgvForZone, type PreviewZone } from "../../lib/alarmPreview";
import type { Member, Thresholds } from "../../lib/types";
import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";

import styles from "./AlarmTestConsole.module.css";

const ZONES: { zone: PreviewZone; label: string }[] = [
  { zone: "urgent_low", label: "Urgent Low" },
  { zone: "low", label: "Low" },
  { zone: "in_range", label: "In Range" },
  { zone: "high", label: "High" },
  { zone: "urgent_high", label: "Urgent High" },
];

interface ScenarioStep {
  zone: PreviewZone;
  label: string;
  waitMs: number;
  // Cycle 82: optional reading-timestamp offset (ms relative to the
  // scenario's start instant; negative = backdated). The recovery
  // repros back-date readings so the engine's reading-timestamp dwell
  // is satisfied deterministically without a 90 s real-time wait.
  atOffsetMs?: number;
}

// Cycle 82: server-authored recovery dwell, mirrored from
// `alerts_service.RECOVERY_DWELL_SECONDS` so the repro spans a hair
// over the real dwell.
const RECOVERY_DWELL_SECONDS = 90;

// Canonical Stop/sync repro: low → wait → (real episode Stop) →
// urgent_low (engine re-arm) → wait → Stop → in_range (recovery).
// The console handles the inject() side; the user presses the real
// in-app Stop button between phases to exercise the actual `stop()`
// path under test.
const SCENARIO_STEPS: ScenarioStep[] = [
  { zone: "low", label: "Inject low — alarm should sound", waitMs: 8000 },
  {
    zone: "urgent_low",
    label: "Inject urgent_low — should engine-re-arm",
    waitMs: 8000,
  },
  {
    zone: "in_range",
    label: "Inject in_range — should recover",
    waitMs: 4000,
  },
];

// Cycle 82 server-authored recovery-dwell repro. Exercises the ENGINE
// recovery path (not a local dwell). Readings are back-dated so the
// engine's reading-timestamp dwell (`RECOVERY_DWELL_SECONDS`) is
// satisfied deterministically on the 3rd injection — no 90 s wait:
//
//   1. low  @ -(dwell+20)s — alarm fires; banner shows Snooze + Stop.
//   2. in_range @ -(dwell+15)s — back across target_low; the in-range
//      run STARTS but its span is still 0, so the episode stays live
//      (cycle-81 dwell hold: audioOn + isLive true, control visible).
//   3. in_range @ -5s — the contiguous in-range run now spans ~(dwell+
//      10)s ≥ the dwell, so the engine stamps `recovered_at` on the
//      open row and broadcasts `alert_recovered`. EVERY connected
//      device clears together (audio off, banner unmounts).
const DWELL_OFFSET = (RECOVERY_DWELL_SECONDS + 20) * 1000;
const DWELL_STEPS_LOW: ScenarioStep[] = [
  {
    zone: "low",
    label: "Inject low (back-dated) — alarm sounds; Snooze + Stop shown",
    waitMs: 6000,
    atOffsetMs: -DWELL_OFFSET,
  },
  {
    zone: "in_range",
    label:
      "Back across target_low — in dwell: audioOn + isLive true, " +
      "control stays (run span still < dwell)",
    waitMs: 6000,
    atOffsetMs: -(DWELL_OFFSET - 5000),
  },
  {
    zone: "in_range",
    label:
      "Dwell span elapses → engine stamps recovered_at + emits " +
      "alert_recovered → all devices clear together",
    waitMs: 4000,
    atOffsetMs: -5000,
  },
];

// Mirror for the `high` side: high → back across target_high → recover.
const DWELL_STEPS_HIGH: ScenarioStep[] = [
  {
    zone: "high",
    label: "Inject high (back-dated) — banner shows Snooze + Stop",
    waitMs: 6000,
    atOffsetMs: -DWELL_OFFSET,
  },
  {
    zone: "in_range",
    label: "Back across target_high — in dwell: control stays",
    waitMs: 6000,
    atOffsetMs: -(DWELL_OFFSET - 5000),
  },
  {
    zone: "in_range",
    label: "Dwell elapses → engine recovers → all devices clear together",
    waitMs: 4000,
    atOffsetMs: -5000,
  },
];

interface Props {
  activeMember: Member | null;
  thresholds: Thresholds;
  episode: AlarmEpisodeState;
  /** Cycle 82: the bound alert row's server-authored `recovered_at`
   *  (from `socket.lastAlert`), shown in the live-state readout so the
   *  operator can watch the engine stamp recovery. */
  recoveredAt?: string | null;
}

export function AlarmTestConsole({
  activeMember,
  thresholds,
  episode,
  recoveredAt,
}: Props): JSX.Element {
  const [busyZone, setBusyZone] = useState<PreviewZone | null>(null);
  const [scenarioStep, setScenarioStep] = useState<number | null>(null);
  // Cycle 81: the scenario runner is now generic over a step array so
  // the Stop/sync repro and the recovery-dwell repro share one engine.
  // `activeSteps` backs the status-line label for whichever is running.
  const [activeSteps, setActiveSteps] = useState<ScenarioStep[]>(
    SCENARIO_STEPS,
  );
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastInjected, setLastInjected] = useState<{
    zone: PreviewZone;
    sgv: number;
    at: string;
  } | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  // Re-render once a second so the live `snoozeRemainingMs` ticks
  // visibly even when no other state changes. Cheap; this whole
  // surface is dev-only.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const cancelScenarioRef = useRef<(() => void) | null>(null);

  const inject = useCallback(
    async (zone: PreviewZone, at?: string) => {
      setLastError(null);
      if (!activeMember) {
        setLastError("no active member");
        return null;
      }
      const sgv = sgvForZone(zone, thresholds);
      setBusyZone(zone);
      const r = await devApi.injectReading(activeMember.id, sgv, "Flat", at);
      setBusyZone(null);
      if (!r.ok) {
        setLastError(
          r.error.kind === "not_found"
            ? "dev harness disabled (404) — set SUGALARM_DEV_INJECT_ENABLED=1"
            : r.error.message,
        );
        return null;
      }
      setLastInjected({
        zone,
        sgv,
        at: new Date().toLocaleTimeString(undefined, { hour12: false }),
      });
      return r.data;
    },
    [activeMember, thresholds],
  );

  const runSteps = useCallback(
    async (steps: ScenarioStep[]) => {
      if (scenarioStep != null) return; // already running
      if (!activeMember) {
        setLastError("no active member");
        return;
      }
      setActiveSteps(steps);
      // Cycle 82: anchor back-dated reading timestamps to one instant
      // so a multi-step scenario's `atOffsetMs` values stay coherent
      // across the waits.
      const base = Date.now();
      let cancelled = false;
      cancelScenarioRef.current = () => {
        cancelled = true;
      };
      for (let i = 0; i < steps.length; i++) {
        if (cancelled) break;
        setScenarioStep(i);
        const step = steps[i]!;
        const at =
          step.atOffsetMs != null
            ? new Date(base + step.atOffsetMs).toISOString()
            : undefined;
        await inject(step.zone, at);
        // Soft wait — short enough that the operator stays engaged,
        // long enough that the WS frame round-trips and the audio
        // bridge settles. The operator is expected to press the real
        // in-app Stop button during these waits (Stop/sync repro), or
        // to just watch the surface (recovery-dwell repro).
        await new Promise((resolve) =>
          window.setTimeout(resolve, steps[i]!.waitMs),
        );
      }
      setScenarioStep(null);
      cancelScenarioRef.current = null;
    },
    [scenarioStep, activeMember, inject],
  );

  const stopScenario = useCallback(() => {
    if (cancelScenarioRef.current) cancelScenarioRef.current();
    setScenarioStep(null);
  }, []);

  const cleanup = useCallback(async () => {
    if (!activeMember) return;
    setLastError(null);
    const r = await devApi.cleanupSynthetic(activeMember.id);
    if (!r.ok) {
      setLastError(r.error.message);
      return;
    }
    setLastInjected(null);
  }, [activeMember]);

  if (collapsed) {
    return (
      <div className={styles.collapsedBar}>
        <span className={styles.devTag}>DEV</span>
        <span className={styles.collapsedLabel}>Alarm Test Console</span>
        <button
          type="button"
          className={styles.expandBtn}
          onClick={() => setCollapsed(false)}
        >
          show
        </button>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.devTag}>DEV</span>
        <span className={styles.title}>Alarm Test Console</span>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={() => setCollapsed(true)}
        >
          hide
        </button>
      </div>
      <div className={styles.hint}>
        Synthetic readings drive the real alerts pipeline. Push
        suppressed. Use the in-app Stop / Snooze controls to exercise
        cross-device behavior.
      </div>
      <div className={styles.btnRow}>
        {ZONES.map(({ zone, label }) => {
          const synthetic = sgvForZone(zone, thresholds);
          return (
            <button
              key={zone}
              type="button"
              className={styles.zoneBtn}
              disabled={busyZone === zone || !activeMember}
              onClick={() => void inject(zone)}
              title={`Inject ${label} @ ${synthetic} mg/dL`}
            >
              {label}
              <span className={styles.zoneBtnSgv}>{synthetic}</span>
            </button>
          );
        })}
      </div>
      <div className={styles.scenarioRow}>
        {scenarioStep == null ? (
          <>
            <button
              type="button"
              className={styles.scenarioBtn}
              disabled={!activeMember}
              onClick={() => void runSteps(SCENARIO_STEPS)}
            >
              Run Stop/sync scenario
            </button>
            {/* Cycle 81: recovery-dwell repros — drive an alarm, then
                step the reading back across the threshold and HOLD in
                the dwell window; a control must stay visible while
                audioOn is true. */}
            <button
              type="button"
              className={styles.scenarioBtn}
              disabled={!activeMember}
              onClick={() => void runSteps(DWELL_STEPS_LOW)}
              title="Low → step back across target_low → hold in recovery-dwell"
            >
              Run low recovery-dwell
            </button>
            <button
              type="button"
              className={styles.scenarioBtn}
              disabled={!activeMember}
              onClick={() => void runSteps(DWELL_STEPS_HIGH)}
              title="High → step back across target_high → hold in recovery-dwell"
            >
              Run high recovery-dwell
            </button>
          </>
        ) : (
          <button
            type="button"
            className={styles.scenarioBtn}
            onClick={stopScenario}
          >
            Stop scenario (step {scenarioStep + 1}/{activeSteps.length})
          </button>
        )}
        <button
          type="button"
          className={styles.cleanupBtn}
          disabled={!activeMember}
          onClick={() => void cleanup()}
          title="Delete synthetic rows minted by this process"
        >
          Clean up
        </button>
      </div>
      {scenarioStep != null ? (
        <div className={styles.scenarioStatus}>
          → {activeSteps[scenarioStep]!.label}
        </div>
      ) : null}
      {lastInjected ? (
        <div className={styles.injected}>
          last: {lastInjected.zone} · {lastInjected.sgv} mg/dL · {lastInjected.at}
        </div>
      ) : null}
      {lastError ? <div className={styles.error}>{lastError}</div> : null}
      <div className={styles.readout}>
        <div className={styles.readoutTitle}>Live episode state</div>
        <dl className={styles.readoutGrid}>
          <dt>phase</dt>
          <dd>{episode.phase}</dd>
          <dt>level</dt>
          <dd>{episode.level ?? "—"}</dd>
          <dt>sgv</dt>
          <dd>{episode.sgv ?? "—"}</dd>
          <dt>audioOn</dt>
          <dd>{String(episode.audioOn)}</dd>
          <dt>silenced</dt>
          <dd>{String(episode.silenced)}</dd>
          <dt>stopped</dt>
          <dd>{String(episode.stopped)}</dd>
          <dt>hasSnoozed</dt>
          <dd>{String(episode.hasSnoozed)}</dd>
          <dt>snoozeRemaining</dt>
          <dd>
            {episode.snoozeRemainingMs != null
              ? `${Math.ceil(episode.snoozeRemainingMs / 1000)} s`
              : "—"}
          </dd>
          <dt>alertId</dt>
          <dd className={styles.mono}>
            {episode.alertId ? episode.alertId.slice(0, 8) : "—"}
          </dd>
          <dt>alarmActive</dt>
          <dd>{String(episode.alarmActive)}</dd>
          {/* Cycle 81: the phase-live control gate + persisted episode
              level. During the recovery-dwell hold, isLive stays true
              and a control must be on screen even though `level`
              (the live reading zone) has gone back to in-range. */}
          <dt>isLive</dt>
          <dd>{String(episode.isLive)}</dd>
          <dt>episodeLevel</dt>
          <dd>{episode.episodeLevel ?? "—"}</dd>
          {/* Cycle 82: server-authored recovery stamp on the bound
              alert row. When the engine recovers the episode, this
              flips non-null and `phase` (server-driven now) returns to
              "idle" on every device together. */}
          <dt>recovered_at</dt>
          <dd className={styles.mono}>
            {recoveredAt
              ? new Date(recoveredAt).toLocaleTimeString(undefined, {
                  hour12: false,
                })
              : "—"}
          </dd>
        </dl>
      </div>
    </div>
  );
}

